import React, { useState } from 'react';
import { Table, Form, Alert } from 'react-bootstrap';

function MedicationTable({ medications, onMedicationChange, onSaveTestCases }) {
  const [editableMedications, setEditableMedications] = useState(medications);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [saveStatus, setSaveStatus] = useState(null);

  const handleChange = (medicationName, field, value) => {
    const updatedMedications = {
      ...editableMedications,
      [medicationName]: {
        ...editableMedications[medicationName],
        [field]: value
      }
    };
    setEditableMedications(updatedMedications);
    if (onMedicationChange) {
      onMedicationChange(updatedMedications);
    }
  };

  const handleRowSelect = (medicationName) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(medicationName)) {
      newSelected.delete(medicationName);
    } else {
      newSelected.add(medicationName);
    }
    setSelectedRows(newSelected);
  };

  const handleSaveTestCases = async () => {
    try {
      setSaveStatus(null); // Clear previous status
      const selectedTestCases = Array.from(selectedRows).map(medicationName => ({
        medication: medicationName,
        details: editableMedications[medicationName],
        timestamp: new Date().toISOString()
      }));
      
      if (onSaveTestCases) {
        await onSaveTestCases(selectedTestCases);
        setSaveStatus({ type: 'success', message: 'Test cases saved successfully!' });
        setSelectedRows(new Set()); // Clear selected rows
      }
    } catch (error) {
      setSaveStatus({ type: 'danger', message: 'Failed to save test cases. Please try again.' });
    }
  };

  return (
    <div>
      {saveStatus && (
        <Alert 
          variant={saveStatus.type} 
          className="mb-3"
          onClose={() => setSaveStatus(null)} 
          dismissible
        >
          {saveStatus.message}
        </Alert>
      )}
      <Table striped bordered hover size="sm">
        <thead>
          <tr>
            <th style={{ width: '5%' }}></th>
            <th style={{ width: '20%' }}>Medication</th>
            <th style={{ width: '15%' }}>Pause Days</th>
            <th style={{ width: '15%' }}>Resume Days</th>
            <th style={{ width: '45%' }}>Bridge</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(editableMedications).map(([medicationName, details]) => (
            <tr key={medicationName}>
              <td>
                <Form.Check
                  type="checkbox"
                  checked={selectedRows.has(medicationName)}
                  onChange={() => handleRowSelect(medicationName)}
                />
              </td>
              <td>{medicationName.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</td>
              <td>
                <Form.Control
                  type="number"
                  value={details.pause_days || ''}
                  onChange={(e) => handleChange(medicationName, 'pause_days', e.target.value ? parseInt(e.target.value) : null)}
                  size="sm"
                />
              </td>
              <td>
                <Form.Control
                  type="number"
                  value={details.resume_days || ''}
                  onChange={(e) => handleChange(medicationName, 'resume_days', e.target.value ? parseInt(e.target.value) : null)}
                  size="sm"
                />
              </td>
              <td style={{ minWidth: '200px' }}>
                <Form.Control
                  as="textarea"
                  value={details.bridge || ''}
                  onChange={(e) => handleChange(medicationName, 'bridge', e.target.value)}
                  size="sm"
                  style={{ 
                    resize: 'none',
                    height: '38px',
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word'
                  }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <div className="d-flex justify-content-end mt-3">
        <button
          className="btn btn-primary"
          onClick={handleSaveTestCases}
          disabled={selectedRows.size === 0}
        >
          Save Test Cases ({selectedRows.size})
        </button>
      </div>
    </div>
  );
}

export default MedicationTable; 